import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from '@albert-io/models';
import {
  masqueradeSchoolFilter,
  readMasqueradeScope,
  type MasqueradeScope
} from 'generic/Masquerade/masqueradeScope';

/**
 * The teacher's active classrooms with their subjects.
 *
 * Caution: because of how long it takes (about 3 seconds) this query is shared across the subject
 * pages and read back by key (e.g. SubjectCourseCard), so every caller must build the same key.
 *
 * @param masquerade Render callers pass it bridged with `useFluxStore`; everyone else omits it and
 *   the store is read directly.
 */
export function getActiveClassroomsWithSubjectsQuery(masquerade?: MasqueradeScope) {
  const {teacherId, schoolId} = masquerade ?? readMasqueradeScope();
  return resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
    .sort('name')
    .skipValidation({sort: true}) // TODO: Remove this and properly mock allowsSorts in SubjectCourseCard.component.test
    .include('assignments_v3')
    .include('subjects_v2')
    .customQuery({
      meta: {
        context: {
          // Drives classroom.isLicensed() (school active license + teacher approved there + using
          // seats) — the per-class gate FreePracticeOptions uses to enable/disable each toggle.
          is_licensed: true,
          classroom: {
            counts: true,
            licensed_subjects: true
          }
        }
      }
    })
    .withMeta('classroom_v1')
    .filter({status: 'active', ...masqueradeSchoolFilter(schoolId)})
    .pageSize(250);
}

/**
 * The teacher's classrooms, archived included, with their subjects' curriculum areas and guides — the
 * source for the class-subject grids on the dashboard's Subjects page and the Library.
 *
 * @param masquerade Render callers pass it bridged with `useFluxStore`; everyone else omits it and
 *   the store is read directly.
 */
export function getClassroomsWithSubjectGuidesQuery(masquerade?: MasqueradeScope) {
  const {teacherId, schoolId} = masquerade ?? readMasqueradeScope();
  return resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .filter({school_v5: schoolId}, Boolean(schoolId))
    .pageSize(250);
}

export function getClassroomWithSubjectsQuery(classroomId: string) {
  return resource('classrooms_v1')
    .mandarkEndpoint(['classrooms_v1', classroomId])
    .include('subjects_v2')
    .filter({status: 'active'})
    .pageSize(250);
}

export function addSubjectToClassroomQuery(classroom: ClassroomModelV1, subjectId: string) {
  return classroom
    .addRelationship({
      relation: [subjectId],
      type: 'subjects_v2'
    })
    .save();
}
